'use client';

import React, { ChangeEvent } from 'react';
import { AnimatePresence, motion, useMotionValue, useTransform } from 'motion/react';
import './Radio.css';

export interface RadioProps {
  label: string;
  value: string | number;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
}

export const Radio = React.memo(function Radio({
  label = 'Value',
  value,
  name = '',
  required = false,
  disabled = false,
  checked,
  onChange,
  ariaLabel,
}: RadioProps) {
  const scale = useMotionValue(1);
  const borderWidth = useTransform(scale, [0.95, 1.05], [3, 1.5]);

  return (
    <div className="radio" data-disabled={disabled} aria-label={ariaLabel}>
      <label>
        <input
          type="radio"
          value={value}
          name={name}
          required={required}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
        />
        <motion.span
          className="radio__button"
          style={{ scale, willChange: 'transform' }}
          whileHover={{ scale: disabled ? 1 : 1.05 }}
          whileTap={{ scale: disabled ? 1 : 0.95 }}
          aria-hidden="true"
          data-disabled={disabled}
        >
          <AnimatePresence>
            {checked && (
              <span className="radio__indicator">
                <motion.span
                  className="radio__border"
                  style={{ borderWidth }}
                  initial={false}
                  animate={{ borderColor: 'var(--color-alias-neutral-lightest)', scale: 1 }}
                  exit={{ borderColor: 'rgba(255, 255, 255, 0)', scale: 0.95 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                />
                <motion.span
                  className="radio__dot"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                />
              </span>
            )}
          </AnimatePresence>
        </motion.span>
        <span className="radio__label">{label}</span>
      </label>
    </div>
  );
});
